import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import type { KitIcon } from "./IconTile";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "link" | "danger";

const CLASS: Record<ButtonVariant, string> = {
  primary: "btn btn-primary",
  secondary: "btn",
  ghost: "btn btn-quiet",
  link: "link-btn",
  danger: "btn btn-danger",
};

/** The class names for a variant, for the odd place that needs them on another element. */
export function buttonClass(variant: ButtonVariant = "secondary", small = false): string {
  return `${CLASS[variant]}${small && variant !== "link" ? " btn-small" : ""}`;
}

type Props = {
  variant?: ButtonVariant;
  small?: boolean;
  /** A trailing icon (ArrowRight for "go on"). */
  icon?: KitIcon;
  /** A leading icon. */
  lead?: KitIcon;
  /** Render as a router link instead of a button. */
  to?: string;
  children: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">;

/**
 * Button: the generic .btn classes with variants. primary = action fill with a soft seal halo on hover (one per
 * view); secondary = surface with a rule border; ghost = no border until hover; link = underlined text; danger =
 * destructive. With `to` it renders a router Link that looks the same.
 */
export function Button({ variant = "secondary", small = false, icon: Icon, lead: Lead, to, children, className, type = "button", ...rest }: Props) {
  const cls = `${buttonClass(variant, small)}${className ? ` ${className}` : ""}`;
  const inner = (
    <>
      {Lead && <Lead size={16} strokeWidth={2} aria-hidden="true" focusable="false" />}
      {children}
      {Icon && <Icon size={16} strokeWidth={2} aria-hidden="true" focusable="false" />}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cls} aria-label={rest["aria-label"]}>
        {inner}
      </Link>
    );
  }
  return (
    <button type={type} className={cls} {...rest}>
      {inner}
    </button>
  );
}
